import { useState, useMemo, useCallback } from 'react';
import type { CsvRow, SortCol, SortDir, AppMessage } from '../tools/types';
import { parseCsvFile } from '../utils/csv';
import { sortRows, filterRows } from '../utils/sort';

export const PAGE_SIZE = 15;

export function useCsvTable() {
  const [rows, setRows]           = useState<CsvRow[]>([]);
  const [extraCols, setExtraCols] = useState<string[]>([]);
  const [messages, setMessages]   = useState<AppMessage[]>([]);
  const [hasData, setHasData]     = useState(false);
  const [loading, setLoading]     = useState(false);

  const [sortCol, setSortCol]     = useState<SortCol>('name');
  const [sortDir, setSortDir]     = useState<SortDir>('asc');
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);

  const handleFile = useCallback(async (file: File) => {
    setLoading(true);
    setMessages([]);
    const result = await parseCsvFile(file);
    setLoading(false);
    setMessages(result.messages);
    if (!result.fatal) {
      setRows(result.rows);
      setExtraCols(result.extraCols);
      setHasData(true);
      setSortCol('name');
      setSortDir('asc');
      setSearch('');
      setPage(1);
    }
  }, []);

  const reset = useCallback(() => {
    setRows([]);
    setExtraCols([]);
    setMessages([]);
    setHasData(false);
    setSortCol('name');
    setSortDir('asc');
    setSearch('');
    setPage(1);
  }, []);

  const handleSort = useCallback((col: SortCol) => {
    if (col === sortCol) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
    setPage(1);
  }, [sortCol]);

  const handleSearch = useCallback((q: string) => {
    setSearch(q);
    setPage(1);
  }, []);

  const filtered = useMemo(() => filterRows(rows, search), [rows, search]);
  const sorted   = useMemo(() => sortRows(filtered, sortCol, sortDir), [filtered, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paginated  = useMemo(
    () => sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [sorted, safePage]
  );

  const uniqueNames = useMemo(
    () => new Set(rows.map(r => r.name.toLowerCase())).size,
    [rows]
  );

  return {
    rows, extraCols, messages, hasData, loading,
    sortCol, sortDir, search, page: safePage, totalPages,
    filtered, paginated, uniqueNames,
    handleFile, reset, handleSort, handleSearch, setPage,
  };
}
